import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppConfig } from './AppConfigProvider';
import { useUser } from './UserProvider';

// Keeps track of inflight GET requests so identical calls return the same promise.
const inflightRequests = new Map<string, Promise<unknown>>();

const buildDedupKey = (
  method: string,
  url: string,
  token?: string,
  extraHeaders?: Record<string, string>
) => {
  const normalizedExtras = extraHeaders
    ? Object.entries(extraHeaders)
        .filter(([key]) => key.toLowerCase() !== 'authorization')
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}:${value}`)
        .join('|')
    : '';
  return `${method}:${url}:${token ?? ''}:${normalizedExtras}`;
};

export function useApi() {
  const { data: config } = useAppConfig();
  const [, userActions] = useUser();
  const apiUrl = config?.apiUrl || '';
  const authGetterRef = useRef(userActions.getAuthToken);

  useEffect(() => {
    authGetterRef.current = userActions.getAuthToken;
  }, [userActions]);

  const request = useCallback(
    async (
      method: string,
      path: string,
      body?: unknown,
      extraHeaders?: Record<string, string>
    ) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(extraHeaders || {}),
      };
      const token = await authGetterRef.current();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const url = `${apiUrl}${path}`;
      const shouldDedup = method.toUpperCase() === 'GET';
      const dedupKey = shouldDedup
        ? buildDedupKey(method, url, headers['Authorization'], extraHeaders)
        : null;

      if (dedupKey && inflightRequests.has(dedupKey)) {
        return inflightRequests.get(dedupKey)!;
      }

      const execPromise = (async () => {
        const res = await fetch(url, {
          method,
          headers,
          body: body ? JSON.stringify(body) : undefined,
        });
        if (!res.ok) {
          const errText = await res.text();
          throw new Error(errText || 'Request failed');
        }
        return res.json();
      })();

      if (dedupKey) {
        inflightRequests.set(dedupKey, execPromise);
      }

      try {
        return await execPromise;
      } finally {
        if (dedupKey) {
          inflightRequests.delete(dedupKey);
        }
      }
    },
    [apiUrl]
  );

  return useMemo(
    () => ({
      get: (path: string, extraHeaders?: Record<string, string>) =>
        request('GET', path, undefined, extraHeaders),
      post: (
        path: string,
        body: unknown,
        extraHeaders?: Record<string, string>
      ) => request('POST', path, body, extraHeaders),
      patch: (
        path: string,
        body: unknown,
        extraHeaders?: Record<string, string>
      ) => request('PATCH', path, body, extraHeaders),
      del: (path: string, extraHeaders?: Record<string, string>) =>
        request('DELETE', path, undefined, extraHeaders),
    }),
    [request]
  );
}
